import type { ReactNode } from "react";
import type { RichSegment } from "../content";

/**
 * AI 栏目首页各板块共用的小部件：眉题、板块标题、富文本段。
 * 视觉规范来自 docs/ai-data-security-solution.html（客户确认稿）。
 */

export function AiEyebrow({ children, onDark = false }: { children: ReactNode; onDark?: boolean }) {
  return (
    <p
      className={`inline-flex items-center gap-2 text-xs font-bold tracking-[0.14em] uppercase mb-4 ${
        onDark ? "text-[#4db8ff]" : "text-[#1a56db]"
      }`}
    >
      <span
        className={`w-[22px] h-[2px] rounded-full ${
          onDark ? "bg-gradient-to-r from-[#4db8ff] to-[#22d3ee]" : "bg-gradient-to-r from-[#1a56db] to-[#22d3ee]"
        }`}
      />
      {children}
    </p>
  );
}

export function AiSectionHead({
  eyebrow,
  title,
  sub,
  onDark = false,
}: {
  eyebrow: string;
  title: string;
  sub?: string;
  onDark?: boolean;
}) {
  return (
    <div className="max-w-[780px] mb-10 lg:mb-12">
      <AiEyebrow onDark={onDark}>{eyebrow}</AiEyebrow>
      <h2 className={`text-2xl lg:text-[2rem] font-bold tracking-tight leading-snug ${onDark ? "text-white" : "text-[#0d1f3c]"}`}>
        {title}
      </h2>
      {sub && (
        <p className={`mt-5 text-sm lg:text-base leading-relaxed ${onDark ? "text-white/60" : "text-[#475569]"}`}>
          {sub}
        </p>
      )}
    </div>
  );
}

/** 渲染一段富文本：strong 片段在浅色板块用品牌蓝加粗，深色板块用白色加粗 */
export function AiRichText({
  segments,
  onDark = false,
  className = "",
}: {
  segments: RichSegment[];
  onDark?: boolean;
  className?: string;
}) {
  return (
    <p className={className}>
      {segments.map((seg, i) =>
        seg.strong ? (
          <strong key={i} className={onDark ? "text-white font-semibold" : "text-[#1a56db] font-semibold"}>
            {seg.text}
          </strong>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </p>
  );
}

/** 平滑滚动到本面板内的锚点（不用 href="#xx"，避免触发页面级 hash 切 Tab 逻辑） */
export function scrollToAiAnchor(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const NAVBAR_H = 72;
  const top = el.getBoundingClientRect().top + window.scrollY - NAVBAR_H;
  window.scrollTo({ top, behavior: "smooth" });
}
